'use strict';

const identity = require('../crypto/identity');
const vcjwt = require('../crypto/vcjwt');
const didlib = require('../crypto/did');
const api = require('../api');
const { registerFlow, loginFlow } = require('../engineflows');
const session = require('../session');
const devicevault = require('../devicevault');
const views = require('./views');
const backupgate = require('./backupgate');

let authMode = 'register';

function setAuthMode(mode) {
  authMode = mode;
  const isReg = mode === 'register';
  views.setText('auth-title', isReg ? 'Create your keys' : 'Welcome back');
  views.setText('auth-submit', isReg ? 'Generate my keys' : 'Unlock my keys');
  views.setText('auth-toggle-text', isReg ? 'Already have an account?' : "Don't have an account?");
  views.setText('auth-toggle-link', isReg ? 'Sign in' : 'Create one');
}

function friendly(e) {
  const m = (e && e.message) || String(e);
  if (/already exists/i.test(m)) return 'An account with that email already exists — try signing in.';
  if (/invalid email or password/i.test(m) || /decrypt|operation-specific|cipher/i.test(m)) return 'Incorrect email or password.';
  return m;
}

async function handleAuthSubmit() {
  const email = (views.$('email-input').value || '').trim();
  const password = views.$('password-input').value || '';
  if (!email || !password) { views.toast('Enter your email and password.', 'error'); return; }
  const submit = views.$('auth-submit');
  const original = submit.textContent;
  submit.disabled = true;
  submit.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin"></i> Working…';
  try {
    if (authMode === 'register') {
      const created = await registerFlow(email, password, { controllerName: email.split('@')[0] });
      await devicevault.remember(email, created.registration).catch(() => {});
      views.show('backup');
      backupgate.start(created.phrase, () => {
        session.set({ email, did: created.did, handle: created.handle, jwks: created.jwks, keys: created.keys, mnemonic: null });
        enterDashboard();
        views.toast('Welcome — your keys are live.', 'info');
      });
    } else {
      const opened = await loginFlow(email, password);
      await devicevault.remember(email, opened).catch(() => {});
      session.set({ email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
      enterDashboard();
    }
  } catch (e) {
    views.toast(friendly(e), 'error');
  } finally {
    submit.disabled = false;
    submit.textContent = original;
  }
}

function enterDashboard() {
  views.renderDashboard(session.get());
  views.show('dashboard');
  loadAttests();
}

async function handleUnlock() {
  const st = session.get();
  const email = st.email || (views.$('unlock-email') && views.$('unlock-email').dataset.email);
  const password = views.$('unlock-password').value || '';
  if (!email) { views.show('auth'); return; }
  if (!password) { views.toast('Enter your password.', 'error'); return; }
  const btn = views.$('unlock-submit');
  btn.disabled = true;
  try {
    const stored = await devicevault.recall(email);
    if (!stored) throw new Error('no device vault');
    const opened = await identity.unlockIdentity(password, { salt: stored.salt, vaultBlob: stored.vaultBlob });
    session.set({ email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
    views.$('unlock-password').value = '';
    enterDashboard();
  } catch (e) {
    views.toast('Incorrect password.', 'error');
  } finally {
    btn.disabled = false;
  }
}

function handleLock() {
  session.lock();
  const st = session.get();
  const pw = views.$('password-input'); if (pw) pw.value = '';
  const upw = views.$('unlock-password'); if (upw) upw.value = '';
  // Device is remembered, so offer the quick password-only unlock rather than full auth.
  if (st.email) {
    const ue = views.$('unlock-email');
    if (ue) { ue.textContent = st.email; ue.dataset.email = st.email; }
    views.show('unlock');
  } else {
    setAuthMode('login');
    views.show('auth');
  }
  views.toast('Vault locked.', 'info');
}

async function handleAttest() {
  const ta = views.$('attest-input');
  const text = (ta.value || '').trim();
  if (!text) { views.toast('Write a statement to attest.', 'error'); return; }
  const st = session.get();
  if (!st.keys) { views.toast('Unlock your vault first.', 'error'); return; }
  const btn = views.$('attest-sign');
  btn.disabled = true;
  try {
    const jwt = await vcjwt.issueVc({
      issuerDid: st.did, kid: 'key-1', privateKey: st.keys.identity.privateKey,
      subjectId: st.did, type: 'AuthenticContentCredential', claims: { statement: text },
    });
    // bsv.Anchor.sha256Hex is Node-only (createHash); use the WebCrypto digest instead.
    const hash = await didlib.sha256Hex(jwt);
    const anchorPayload = { kind: 'VC_ANCHOR_SHA256', hash, issuerDid: st.did };
    await api.registerAttestation({ hash, handle: st.handle, issuerDid: st.did, anchorPayload }).catch(() => {});
    const v = await vcjwt.verifyVc(jwt, st.jwks);
    const item = { text, jwt, hash, ok: v.valid, revoked: false, ts: Date.now() };
    views.addFeedItem(item);
    persistAttest(st.email, item);
    ta.value = '';
    views.toast('Verifiable credential signed & anchored.', 'info');
  } catch (e) {
    views.toast('Could not sign: ' + ((e && e.message) || e), 'error');
  } finally {
    btn.disabled = false;
  }
}

async function handleRevoke(hash) {
  const st = session.get();
  if (!st.keys || !hash) { views.toast('Unlock your vault first.', 'error'); return; }
  try {
    const sig = window.bsv.Message.sign('revoke:' + hash, st.keys.identity.privateKey);
    await api.revoke({ hash, handle: st.handle, signature: sig });
    markRevoked(st.email, hash);
    loadAttests();
    views.toast('Credential revoked.', 'info');
  } catch (e) {
    views.toast('Revoke failed: ' + ((e && e.message) || e), 'error');
  }
}

// Signed attestations are public data (statement + signature + address), safe to cache.
const attestKey = (email) => 'w3k:attests:' + String(email || '').toLowerCase();
function persistAttest(email, item) {
  try {
    const k = attestKey(email);
    const list = JSON.parse(localStorage.getItem(k) || '[]');
    list.unshift(item);
    localStorage.setItem(k, JSON.stringify(list.slice(0, 50)));
  } catch (_) { /* ignore */ }
}
function markRevoked(email, hash) {
  try {
    const k = attestKey(email);
    const list = JSON.parse(localStorage.getItem(k) || '[]');
    for (const it of list) if (it.hash === hash) it.revoked = true;
    localStorage.setItem(k, JSON.stringify(list));
  } catch (_) { /* ignore */ }
}
function loadAttests() {
  try {
    const st = session.get();
    const feed = views.$('attest-feed');
    if (feed) feed.innerHTML = '';
    const list = JSON.parse(localStorage.getItem(attestKey(st.email)) || '[]');
    list.forEach((item) => views.addFeedItem(item));
  } catch (_) { /* ignore */ }
}

function wireCopies() {
  document.querySelectorAll('[data-copy-target]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const el = views.$(btn.getAttribute('data-copy-target'));
      if (!el) return;
      const text = el.textContent;
      if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
      views.toast('Copied to clipboard.', 'info');
    });
  });
}

async function decideInitialView() {
  try {
    const s = await api.session();
    if (s && s.email) {
      const stored = await devicevault.recall(s.email);
      if (stored) {
        session.set({ email: s.email });
        const ue = views.$('unlock-email');
        if (ue) { ue.textContent = s.email; ue.dataset.email = s.email; }
        views.show('unlock');
        return;
      }
    }
  } catch (_) { /* ignore */ }
  setAuthMode('register');
  views.show('auth');
}

function bootstrap() {
  if (!views.hasUI()) return; // not the app page (e.g., a test harness)

  const form = views.$('auth-form');
  if (form) form.addEventListener('submit', (e) => { e.preventDefault(); handleAuthSubmit(); });
  const submit = views.$('auth-submit');
  if (submit) submit.addEventListener('click', (e) => { e.preventDefault(); handleAuthSubmit(); });
  const toggle = views.$('auth-toggle-link');
  if (toggle) toggle.addEventListener('click', (e) => { e.preventDefault(); setAuthMode(authMode === 'register' ? 'login' : 'register'); });

  const lock = views.$('lock-btn');
  if (lock) lock.addEventListener('click', (e) => { e.preventDefault(); handleLock(); });
  const attest = views.$('attest-sign');
  if (attest) attest.addEventListener('click', (e) => { e.preventDefault(); handleAttest(); });

  // Delegated actions on attestation feed items (copy VC / revoke).
  const feed = views.$('attest-feed');
  if (feed) feed.addEventListener('click', (e) => {
    const copy = e.target.closest('.feed-copy');
    if (copy) { if (navigator.clipboard) navigator.clipboard.writeText(copy.dataset.jwt || '').catch(() => {}); views.toast('VC copied — paste it on the verify page.', 'info'); return; }
    const rev = e.target.closest('.feed-revoke');
    if (rev) { const item = rev.closest('.feed-item'); handleRevoke(item && item.dataset.hash); return; }
  });

  const unlockBtn = views.$('unlock-submit');
  if (unlockBtn) unlockBtn.addEventListener('click', (e) => { e.preventDefault(); handleUnlock(); });
  const unlockDiff = views.$('unlock-different');
  if (unlockDiff) unlockDiff.addEventListener('click', async (e) => {
    e.preventDefault();
    await api.logout().catch(() => {});
    session.clear();
    setAuthMode('login');
    views.show('auth');
  });

  const bio = views.$('biometric-btn');
  if (bio) bio.addEventListener('click', (e) => { e.preventDefault(); views.toast('Biometric unlock arrives in a later release.', 'info'); });

  document.querySelectorAll('[data-launch-app]').forEach((b) => b.addEventListener('click', () => {
    const app = document.getElementById('app-demo');
    if (app) app.scrollIntoView({ behavior: 'smooth' });
    const em = views.$('email-input');
    if (em) setTimeout(() => em.focus(), 400);
  }));

  window.addEventListener('beforeunload', () => { try { session.lock(); } catch (_) { /* ignore */ } });

  wireCopies();
  setAuthMode('register');
  decideInitialView();
}

module.exports = { bootstrap };
